import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { isNotUndefined } from 'lib/helpers/array.utils.js';
import { getLevenshteinDistance } from 'lib/helpers/search.utils.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { match, P } from 'ts-pattern';
import { IButton } from '../buttons/index.js';
import { CoreComponent } from '../core/core.component.js';
import { FlexSize } from '../flex/index.js';
import { ISearchInputResult } from '../form-inputs/search/search-input.component.js';
import { ISingleSeries } from '../graphs/index.js';
import { IconType } from '../icons/index.js';
import { TagListItem } from '../tag/tag-list.component.js';
import { TagType } from '../tag/tag.component.js';
import { IImage } from '../ui-models.js';

export type ListItem<TData = unknown> = {
	readonly id: string;
	readonly data: TData;

	readonly avatar?: IImage;
	readonly iconType?: IconType;
	readonly images?: readonly ListImage[];
	readonly videoUrl?: string;
	readonly imagesLocation?: ListItemImageLocation;
	readonly title: string;
	readonly subtitle?: string | readonly string[];
	readonly routerLink?: string;
	readonly externalUrlLink?: string;
	readonly tag?: ListTag;
	readonly fromNowDate?: string;
	readonly buttons?: readonly IButton[];
	readonly pieGraph?: ListPieGraph;
	readonly select?: () => void;
	readonly isSelected?: boolean;
};

export type ListTag = {
	readonly text: string;
	readonly tagType: TagType;
};

export type ListCategory<TData = unknown> = {
	readonly title: string;
	readonly filterItems: (items: readonly ListItem<TData>[]) => readonly ListItem<TData>[];
};

export type ListItemImageLocation = 'default' | 'end';
export type ListDesign = 'default' | 'selectionList';
export type ListSortOrder = 'desc' | 'asc';
export type ListAvatarMode = 'both' | 'preferImage' | 'preferIcon';
export type ListTagLocation = 'end' | 'inline';

export type ListPieGraph = {
	readonly singleSeries: readonly ISingleSeries[];
	readonly colorScheme?: readonly string[];
};

export type ListImage = IImage;

export type ListAvatarSize = 'default' | '50px';

type CalculatedSizeOfAvatarAndIcon = {
	readonly flex: FlexSize;
	readonly widthAndHeight: number;
};

type ExtendedItem<TItem> = ListItem<TItem> & {
	readonly filteredImages: readonly ListImage[];
	readonly mainIcon: IconType | undefined;
	readonly selectIcon: IconType | undefined;
	readonly mainAvatar: IImage | undefined;
	readonly subtitle: string[] | undefined;
};

@Component({
	selector: 'lib-list',
	templateUrl: './list.component.html',
	styleUrls: ['./list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ListComponent<TItem = unknown> extends CoreComponent {
	public readonly avatarSize = input<ListAvatarSize>('default');
	public readonly avatarMode = input<ListAvatarMode>('preferImage');
	public readonly items = input.required<readonly ListItem<TItem>[] | undefined>();
	public readonly categories = input<readonly ListCategory<TItem>[] | undefined>();
	public readonly pageSize = input<number>(50);
	public readonly selectedItemId = input<string>();
	public readonly noItemsText = input<string>();
	public readonly enableSearch = input<boolean>(false);
	public readonly hideSelectIcon = input<boolean>(false);
	public readonly showNameBasedAvatar = input<boolean>(false);
	public readonly design = input<ListDesign>('default');
	public readonly disableSort = input<boolean>(false);
	public readonly sortOrder = input<ListSortOrder>('desc');
	public readonly tagLocation = input<ListTagLocation>('end');
	public readonly isInDialog = input<boolean>(false);
	public readonly enableMultiSelect = input<boolean>(false);

	public readonly selectedCategory = signal<ListCategory<TItem> | undefined>(undefined);
	public readonly categoryTags = computed<readonly TagListItem[] | undefined>(() => {
		const categories = this.categories();
		if (!categories) {
			return undefined;
		}

		const selectedCategory = this.selectedCategory();

		return [
			{
				text: 'All',
				tagType: selectedCategory ? 'basic' : 'primary',
				click: () => {
					this.selectedCategory.set(undefined);
				}
			},
			...categories.map<TagListItem>((m) => {
				return {
					text: m.title,
					tagType: selectedCategory?.title === m.title ? 'primary' : 'basic',
					click: () => {
						this.selectedCategory.set(m);
					}
				};
			})
		];
	});

	public readonly sizeOfAvatarAndIcon = computed<CalculatedSizeOfAvatarAndIcon>(() => {
		if (this.avatarSize() === '50px') {
			return {
				flex: '50px',
				widthAndHeight: 50
			};
		}

		if (this.isMobileView()) {
			return {
				flex: '40px',
				widthAndHeight: 40
			};
		}

		return {
			flex: '45px',
			widthAndHeight: 45
		};
	});

	public readonly calculatedTagLocation = computed<ListTagLocation>(() => {
		if (this.isMobileView()) {
			return 'inline';
		}
		return this.tagLocation();
	});

	public readonly selectItem = output<ListItem<TItem>>();

	private readonly showSearchItemsThreshold: number = 10;
	private readonly search = signal<ISearchInputResult | undefined>(undefined);
	public readonly filteredItems = computed<readonly ListItem<TItem>[] | undefined>(() => {
		return this.getFilteredItems();
	});

	public readonly selectedItemIds = signal<readonly string[]>([]);
	private readonly anyItemWasSelected = signal<boolean>(false);

	public readonly displayedItems = computed<readonly ExtendedItem<TItem>[] | undefined>(() => {
		const pageSize = this.pageSize();
		let displayedItems = this.filteredItems();

		if (!displayedItems) {
			return undefined;
		}

		if (pageSize && pageSize > 0) {
			const takeItems = this.displayedItemsPage() * pageSize;
			displayedItems = displayedItems.slice(0, takeItems);
		}

		return displayedItems.map((m) => {
			const filteredImages: readonly ListImage[] = match(this.isMobileView())
				.with(true, () => (m.images?.length ? [m.images[0]] : []))
				.with(false, () => m.images ?? [])
				.exhaustive();

			const extendedItem: ExtendedItem<TItem> = {
				...m,
				filteredImages: filteredImages,
				mainIcon: m.iconType,
				selectIcon: match({ design: this.design(), hideSelectIcon: this.hideSelectIcon() })
					.returnType<IconType | undefined>()
					.with({ hideSelectIcon: true }, () => undefined)
					.with({ design: 'selectionList' }, () => 'select')
					.with({ design: 'default' }, () => undefined)
					.otherwise(() => undefined),
				mainAvatar: this.getMainAvatar(m),
				subtitle: Array.isArray(m.subtitle) ? m.subtitle : m.subtitle ? [m.subtitle] : undefined
			};

			return extendedItem;
		});
	});

	public readonly displayedItemsPage = signal<number>(1);

	public readonly showLoadMore = computed<boolean>(() => {
		const displayedItemsCount = this.displayedItems()?.length ?? 0;
		const filteredItemsCount = this.filteredItems()?.length ?? 0;

		if (displayedItemsCount < filteredItemsCount) {
			return true;
		}

		return false;
	});

	public readonly showLoadMoreCount = computed<number>(() => {
		const pageSize = this.pageSize();

		if (!pageSize) {
			return 0;
		}

		const displayedItemsCount = this.displayedItems()?.length ?? 0;
		const filteredItemsCount = this.filteredItems()?.length ?? 0;

		const remainingCount = filteredItemsCount - displayedItemsCount;

		if (remainingCount > pageSize) {
			return pageSize;
		}

		return remainingCount;
	});

	public readonly useSearch = computed<boolean>(() => {
		const items = this.items();
		const enableSearch = this.enableSearch();

		if (!enableSearch || !items) {
			return false;
		}

		if (items.length > this.showSearchItemsThreshold) {
			return true;
		}
		return false;
	});

	protected readonly showDivider = computed<boolean>(() => {
		return this.design() !== 'selectionList';
	});

	protected readonly dialogClassNames = computed<readonly string[]>(() => {
		const classes: readonly string[] = [
			this.design(),
			this.isMobileView() ? 'mobile' : 'desktop',
			this.design() === 'selectionList' ? 'selection-list' : undefined,
			this.isInDialog() ? 'dialog' : undefined
		].filter(isNotUndefined);

		return classes;
	});

	constructor() {
		super();

		effect(() => {
			// reset search when items change
			const items = this.items();
			this.search.set(undefined);

			// only set selected items for the first
			if (items && !this.anyItemWasSelected()) {
				this.selectedItemIds.set(items.filter((m) => m.isSelected).map((m) => m.id));
			}
		});
	}

	showImages(item: ExtendedItem<TItem>): boolean {
		if (item.filteredImages?.length) {
			return true;
		}
		return false;
	}

	handleSearch(search: ISearchInputResult): void {
		this.search.set(search);

		// reset page
		this.displayedItemsPage.set(1);
	}

	handleSelectItem(item: ListItem<TItem>): void {
		if (this.design() !== 'selectionList') {
			return;
		}

		this.anyItemWasSelected.set(true);

		const isSelected = !this.selectedItemIds().includes(item.id);

		this.selectedItemIds.update((m) => {
			const itemsWithoutItem = m.filter((m) => m !== item.id);

			if (isSelected) {
				return [...itemsWithoutItem, item.id];
			}
			return itemsWithoutItem;
		});
		item.select?.();
		this.selectItem.emit(item);
	}

	handleLoadMore(): void {
		this.displayedItemsPage.update((value) => value + 1);
	}

	private getMainAvatar(item: ListItem<TItem>): IImage | undefined {
		return item.avatar;
	}

	private getFilteredItems(): readonly ListItem<TItem>[] | undefined {
		const search = this.search();
		const items = this.items();

		if (!items) {
			return undefined;
		}

		const categoryFilteredItems = match(this.selectedCategory())
			.returnType<readonly ListItem<TItem>[]>()
			.with(P.nonNullable, (category) => {
				return category.filterItems(items);
			})
			.otherwise(() => items);

		const filteredItems = match(search)
			.returnType<readonly ListItem<TItem>[]>()
			.with(P.nonNullable, (search) => {
				return categoryFilteredItems.filter((m) => {
					if (m.subtitle) {
						const flattenedSubtitle = match(m.subtitle)
							.returnType<string>()
							.with(P.array(), (m) => m.join(' '))
							.with(P.string, (m) => m)
							.otherwise(() => '');
						return (
							stringHelper.containsAll(m.title, search.searchWords) ||
							stringHelper.containsAll(flattenedSubtitle, search.searchWords)
						);
					}

					return stringHelper.containsAll(m.title, search.searchWords);
				});
			})
			.otherwise(() => categoryFilteredItems);

		// order items only if items are not searched
		if (search?.searchWords.length) {
			return this.sortByRelevance(search, filteredItems);
		}
		return this.sortByDefaultConfig(filteredItems);
	}

	private sortByDefaultConfig(items: readonly ListItem<TItem>[]): readonly ListItem<TItem>[] {
		if (this.disableSort()) {
			return items;
		}
		const sortOrder = this.sortOrder();

		return items.toSorted((v1, v2) => {
			const a: string = sortOrder === 'asc' ? v1.title : v2.title;
			const b: string = sortOrder === 'asc' ? v2.title : v1.title;

			if (a.toLowerCase() < b.toLowerCase()) {
				return -1;
			}
			if (a.toLowerCase() > b.toLowerCase()) {
				return 1;
			}
			return 0;
		});
	}

	private sortByRelevance(search: ISearchInputResult, items: readonly ListItem<TItem>[]): readonly ListItem<TItem>[] {
		return items.toSorted((a, b) => {
			return getLevenshteinDistance(a.title, search.search) - getLevenshteinDistance(b.title, search.search);
		});
	}
}
